import { Invoice, Client } from '../types';
import { apiService } from './apiService';

export interface EmailResult {
  success: boolean;
  message: string;
  error?: string;
}

export class EmailService {
  private static instance: EmailService;
  private emailStatus: 'working' | 'not-working' | 'unknown' = 'unknown';

  static getInstance(): EmailService {
    if (!EmailService.instance) {
      EmailService.instance = new EmailService();
    }
    return EmailService.instance;
  }

  async sendInvoice(invoice: Invoice, client: Client): Promise<EmailResult> {
    try {
      if (!invoice.id) {
        return {
          success: false,
          message: 'La factura debe estar guardada antes de enviarla',
          error: 'ID de factura requerido'
        };
      }
      if (!client.email?.trim()) {
        return {
          success: false,
          message: 'El cliente no tiene email configurado',
          error: 'Email requerido'
        };
      }

      const response = await apiService.sendInvoiceEmail(invoice.id, { to: client.email });
      if (response.error) {
        this.emailStatus = 'not-working';
        return {
          success: false,
          message: response.message || response.error || 'Error al enviar la factura por email',
          error: response.error
        };
      }

      this.emailStatus = 'working';
      return {
        success: true,
        message: response.data?.message || `Factura #${invoice.number} enviada a ${client.email}`
      };
    } catch (error) {
      this.emailStatus = 'not-working';
      return {
        success: false,
        message: 'Error al enviar la factura por email',
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  async sendAutoInvoice(config: any, client: Client): Promise<EmailResult> {
    try {
      // Simular envío de factura automática
      await this.simulateEmailSending();
      
      this.emailStatus = 'working';
      
      return {
        success: true,
        message: `Factura automática enviada exitosamente a ${client.email || 'cliente'}`
      };
    } catch (error) {
      this.emailStatus = 'not-working';
      return {
        success: false,
        message: 'Error al enviar la factura automática por email',
        error: error instanceof Error ? error.message : 'Error desconocido'
      };
    }
  }

  private async simulateEmailSending(): Promise<void> {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));
    
    // Simular fallo ocasional (5% de probabilidad)
    if (Math.random() < 0.05) {
      throw new Error('Error de conexión simulada');
    }
  }

  getEmailStatus(): 'working' | 'not-working' | 'unknown' {
    return this.emailStatus;
  }

  testEmailConnection(): Promise<EmailResult> {
    return this.simulateEmailSending()
      .then(() => {
        this.emailStatus = 'working';
        return {
          success: true,
          message: 'Conexión de email funcionando correctamente'
        };
      })
      .catch((error) => {
        this.emailStatus = 'not-working';
        return {
          success: false,
          message: 'Error en la conexión de email',
          error: error instanceof Error ? error.message : 'Error desconocido'
        };
      });
  }

  // Método para configurar EmailJS (cuando se implemente)
  setupEmailJS(serviceId: string, templateId: string, userId: string): void {
    // Aquí se configuraría EmailJS
    console.log('Configurando EmailJS:', { serviceId, templateId, userId });
  }
} 